import { CircularProgress } from '@mui/material';
import AIWizardPage from 'AIWizardPage';
import BackendService from 'BackendService';
import { CommonLoggedInDataContext, useCommonLoggedInData } from 'CommonLoggedInData';
import FundOpportunityEOI from '../../FundOpportunityEOI';
import FundOpportunityAdmin from 'FundOpportunityAdmin';
import FundOpportunityAdminNew from '../FundOpportunityAdmin';
import FundUpdate from 'FundUpdate';
import FundUpdateWithInvestor from '../FundUpdateWithInvestor';
import IRRManagementPage from 'IRRManagementPage';
import { Permissions } from 'Permission';
import { usePermissions } from 'PermissionContext';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FundOpportunity from '../../../models/FundOpportunity';
import NavigateWithMessage from './NavigateWithMessage';

export interface LookupOpportunityProps {
  type:
    | 'FundOpportunityAdmin'
    | 'FundOpportunityAdminNew'
    | 'FundOpportunity'
    | 'FundUpdate'
    | 'FundUpdateWithInvestor'
    | 'AIWizardPage'
    | 'IRRManagement';
  editMode?: boolean;
  print?: boolean;
  printDraft?: boolean;
}

export default function LookupOpportunity({ type, print, printDraft }: LookupOpportunityProps) {
  const { id } = useParams();
  const [fundOpportunity, setFundOpportunity] = useState({
    data: null as FundOpportunity | null,
  });

  const loggedInData = useCommonLoggedInData();
  const { hasPermission: checkPermission } = usePermissions();

  useEffect(() => {
    const fetchFundOpportunity = () => {
      new BackendService()
        .getFundOpportunity(id!)
        .then((fundOpportunity) => {
          setFundOpportunity(fundOpportunity);
        })
        .catch((error) => {
          console.error('Error fetching fund opportunity:', error);
        });
    };

    fetchFundOpportunity();
  }, [id, type]);

  if (!fundOpportunity.data) {
    return (
      <div>
        <CircularProgress />
      </div>
    );
  }

  return (
    <CommonLoggedInDataContext.Provider value={loggedInData}>
      {(() => {
        switch (type) {
          case 'FundOpportunityAdmin':
            return (
              <FundOpportunityAdmin
                fundOpportunity={fundOpportunity.data}
                hasPermission={checkPermission}
              />
            );
          case 'FundUpdate':
            return <FundUpdate fundOpportunity={fundOpportunity.data} />;
          case 'FundUpdateWithInvestor':
            return <FundUpdateWithInvestor fundOpportunity={fundOpportunity.data} />;
          case 'FundOpportunityAdminNew':
            return (
              <FundOpportunityAdminNew
                fundOpportunity={fundOpportunity.data}
                hasPermission={checkPermission}
              />
            );
          case 'FundOpportunity': {
            // Content creators see the draft (e.g. the print/draft PDF route);
            // everyone else sees the published page.
            const adminView = checkPermission(Permissions.ContentCreation);

            if (fundOpportunity.data.linkedContentExternalId) {
              return (
                <NavigateWithMessage
                  to={`/opportunities/${fundOpportunity.data.linkedContentExternalId}/content`}
                >
                  This content is linked to another opportunity. Navigating to the linked
                  opportunity content.
                </NavigateWithMessage>
              );
            }

            return (
              <FundOpportunityEOI
                fundOpportunity={fundOpportunity.data}
                adminView={adminView}
                print={print}
                printDraft={printDraft}
              />
            );
          }
          case 'AIWizardPage':
            return <AIWizardPage fundOpportunity={fundOpportunity.data} />;
          case 'IRRManagement':
            return <IRRManagementPage fundOpportunity={fundOpportunity.data} />;
          default:
            return null;
        }
      })()}
    </CommonLoggedInDataContext.Provider>
  );
}
